import { useEffect, useMemo, useRef, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Plus,
    Pencil,
    Trash2,
    Eye,
    FileText,
    Loader2,
    Trash,
    List,
    GripVertical,
    Settings2,
} from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { StyleControls } from "@/components/StyleEditor";
import { jdCss } from "@/lib/jdStyles";
import {
    useJdDesign,
    refreshJdDesign,
} from "@/lib/jdDesignStore";

const DEFAULT_SECTION_HEADINGS = [
    "About the Role",
    "Responsibilities",
    "Requirements",
    "Nice to have",
];

const makeSection = (heading = "", content = "") => ({
    _rid: Math.random().toString(36).slice(2),
    heading,
    content,
});

const makeAboutRow = (label = "", value = "") => ({
    _rid: Math.random().toString(36).slice(2),
    label,
    value,
});

const makeHeaderRow = (label = "", value = "") => ({
    _rid: Math.random().toString(36).slice(2),
    label,
    value,
});

const emptyForm = () => ({
    job_role_id: "",
    job_title: "",
    header_rows: [],
    about_rows: [],
    sections: DEFAULT_SECTION_HEADINGS.map((h) => makeSection(h, "")),
    callout_text:
        "We highly encourage you to apply even if you don't match every criterion. If you feel you'd be a strong fit, we'd love to hear from you.",
});

// Style / apply-button controls now live in the shared global JD design
// document (see `lib/jdDesignStore.js`). Every JD renders with the same
// design; only the JD content is per-JD.

export default function CreateJobDescription() {
    const [roles, setRoles] = useState([]);
    const [descriptions, setDescriptions] = useState([]);
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [viewing, setViewing] = useState(null);

    const [editing, setEditing] = useState(null); // description id or null
    const [form, setForm] = useState(emptyForm());
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const roleMap = useMemo(() => {
        const m = {};
        roles.forEach((r) => (m[r.id] = r));
        return m;
    }, [roles]);

    const fetchAll = async () => {
        try {
            const [rolesRes, descRes] = await Promise.all([
                api.get("/job-roles"),
                api.get("/job-descriptions"),
            ]);
            setRoles(rolesRes.data);
            setDescriptions(descRes.data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const openCreate = () => {
        if (roles.length === 0) {
            toast.error(
                "Create at least one Job Role first before adding a description.",
            );
            return;
        }
        setEditing(null);
        setForm(emptyForm());
        setError("");
        setDialogOpen(true);
    };

    const openEdit = (jd) => {
        setEditing(jd.id);
        const nextForm = {
            job_role_id: jd.job_role_id,
            job_title: jd.job_title,
            about_rows: (jd.about_rows || []).map((r) =>
                makeAboutRow(r.label, r.value),
            ),
            header_rows: (jd.header_rows || []).map((r) =>
                makeHeaderRow(r.label, r.value),
            ),
            sections: (jd.sections || []).map((s) =>
                makeSection(s.heading, s.content),
            ),
            callout_text: jd.callout_text ?? "",
        };
        setForm(nextForm);
        setError("");
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditing(null);
        setForm(emptyForm());
        setError("");
    };

    const openView = (jd) => {
        setViewing(jd);
        setViewOpen(true);
    };

    const addSection = () =>
        setForm((f) => ({ ...f, sections: [...f.sections, makeSection()] }));

    // Drag-and-drop reorder helpers — one ref per list so drags don't cross
    // sections (visually rearranging one collection at a time).
    const headerDragIdx = useRef(null);
    const aboutDragIdx = useRef(null);
    const sectionDragIdx = useRef(null);
    const reorder = (key, from, to) => {
        if (from == null || from === to) return;
        setForm((f) => {
            const next = [...f[key]];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return { ...f, [key]: next };
        });
    };

    const removeSection = (rid) =>
        setForm((f) => ({
            ...f,
            sections: f.sections.filter((s) => s._rid !== rid),
        }));

    const updateSection = (rid, patch) =>
        setForm((f) => ({
            ...f,
            sections: f.sections.map((s) =>
                s._rid === rid ? { ...s, ...patch } : s,
            ),
        }));

    // Refs to each section textarea so the bullet-toggle button can operate
    // on the current selection.
    const sectionRefs = useRef({});
    const setSectionRef = (rid) => (el) => {
        if (el) sectionRefs.current[rid] = el;
        else delete sectionRefs.current[rid];
    };

    const bulletifySection = (rid) => {
        const el = sectionRefs.current[rid];
        if (!el) return;
        const value = el.value ?? "";
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? value.length;

        const applyLine = (line) => {
            const stripped = line.replace(/^\s*(?:•\s?|[-*]\s+)/, "");
            const trimmed = stripped.trim();
            if (!trimmed) return line;
            return `•  ${stripped}`;
        };

        let newValue;
        let selStart;
        let selEnd;

        if (start !== end) {
            // Expand selection to full lines so users can highlight a
            // paragraph without worrying about exact line boundaries.
            let s = start;
            let e = end;
            while (s > 0 && value[s - 1] !== "\n") s -= 1;
            while (e < value.length && value[e] !== "\n") e += 1;
            const block = value.slice(s, e);
            const transformed = block.split("\n").map(applyLine).join("\n");
            newValue = value.slice(0, s) + transformed + value.slice(e);
            selStart = s;
            selEnd = s + transformed.length;
        } else {
            const before = value.slice(0, start);
            const needsNewline = before.length > 0 && !before.endsWith("\n");
            const insertion = `${needsNewline ? "\n" : ""}•  `;
            newValue = before + insertion + value.slice(start);
            const caret = start + insertion.length;
            selStart = caret;
            selEnd = caret;
        }

        updateSection(rid, { content: newValue });
        requestAnimationFrame(() => {
            const node = sectionRefs.current[rid];
            if (!node) return;
            node.focus();
            node.setSelectionRange(selStart, selEnd);
        });
    };

    const addAboutRow = () =>
        setForm((f) => ({
            ...f,
            about_rows: [...f.about_rows, makeAboutRow()],
        }));

    const removeAboutRow = (rid) =>
        setForm((f) => ({
            ...f,
            about_rows: f.about_rows.filter((r) => r._rid !== rid),
        }));

    const updateAboutRow = (rid, patch) =>
        setForm((f) => ({
            ...f,
            about_rows: f.about_rows.map((r) =>
                r._rid === rid ? { ...r, ...patch } : r,
            ),
        }));

    const addHeaderRow = () =>
        setForm((f) => ({
            ...f,
            header_rows: [...f.header_rows, makeHeaderRow()],
        }));

    const removeHeaderRow = (rid) =>
        setForm((f) => ({
            ...f,
            header_rows: f.header_rows.filter((r) => r._rid !== rid),
        }));

    const updateHeaderRow = (rid, patch) =>
        setForm((f) => ({
            ...f,
            header_rows: f.header_rows.map((r) =>
                r._rid === rid ? { ...r, ...patch } : r,
            ),
        }));

    // ---- Global JD Design editor state ------------------------------------
    // The design is a single shared config for every JD. The dialog stages
    // a working copy so admins can Cancel without side-effects; Save writes
    // via PUT /jd-design and refreshes every reader across the app.
    const savedDesign = useJdDesign();
    const [designOpen, setDesignOpen] = useState(false);
    const [designTab, setDesignTab] = useState("global");
    const [draftDesign, setDraftDesign] = useState(savedDesign);
    const [designSaving, setDesignSaving] = useState(false);

    // Keep draft in sync when the dialog opens for the first time or the
    // upstream doc changes while the dialog is closed.
    useEffect(() => {
        if (!designOpen) setDraftDesign(savedDesign);
    }, [savedDesign, designOpen]);

    const openDesign = () => {
        setDraftDesign(savedDesign);
        setDesignOpen(true);
    };

    const patchDraftStyle = (key, s) =>
        setDraftDesign((d) => ({
            ...d,
            styles: { ...(d?.styles || {}), [key]: s },
        }));

    const saveDesign = async () => {
        setDesignSaving(true);
        try {
            await api.put("/jd-design", draftDesign);
            await refreshJdDesign();
            toast.success("JD design saved. All JDs updated.");
            setDesignOpen(false);
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setDesignSaving(false);
        }
    };

    const DESIGN_TABS = [
        { id: "global", label: "Global JD" },
        { id: "title", label: "Job Title" },
        { id: "subtitle", label: "Eyebrow" },
        { id: "header_line", label: "Header Line" },
        { id: "callout", label: "Callout" },
        { id: "body_header_label", label: "Body Label" },
        { id: "body_header_value", label: "Body Value" },
        { id: "section_heading", label: "Section Heading" },
        { id: "section_content", label: "Section Content" },
    ];

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.job_role_id) {
            setError("Please select a Job Role.");
            return;
        }
        if (!form.job_title.trim()) {
            setError("Job title is required.");
            return;
        }
        const cleanedSections = form.sections
            .map((s) => ({
                heading: s.heading.trim(),
                content: s.content.trim(),
            }))
            .filter((s) => s.heading || s.content);
        const cleanedAboutRows = form.about_rows
            .map((r) => ({
                label: r.label.trim(),
                value: r.value.trim(),
            }))
            .filter((r) => r.label || r.value);
        const cleanedHeaderRows = form.header_rows
            .map((r) => ({
                label: r.label.trim(),
                value: r.value.trim(),
            }))
            .filter((r) => r.label || r.value);

        setSubmitting(true);
        try {
            const payload = {
                job_role_id: form.job_role_id,
                job_title: form.job_title.trim(),
                about_rows: cleanedAboutRows,
                header_rows: cleanedHeaderRows,
                sections: cleanedSections,
                callout_text: (form.callout_text || "").trim(),
            };
            if (editing) {
                const { data } = await api.put(
                    `/job-descriptions/${editing}`,
                    payload,
                );
                setDescriptions((ds) =>
                    ds.map((d) => (d.id === editing ? data : d)),
                );
                toast.success("Job description updated.");
            } else {
                const { data } = await api.post(
                    "/job-descriptions",
                    payload,
                );
                setDescriptions((ds) => [data, ...ds]);
                toast.success("Job description created.");
            }
            closeDialog();
        } catch (err) {
            setError(apiErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (jd) => {
        if (
            !window.confirm(
                `Delete "${jd.job_title}"? This cannot be undone.`,
            )
        )
            return;
        try {
            await api.delete(`/job-descriptions/${jd.id}`);
            setDescriptions((ds) => ds.filter((d) => d.id !== jd.id));
            toast.success("Job description deleted.");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    return (
        <div data-testid="admin-job-description-page">
            <AdminPageHeader
                eyebrow="Modules / Job Descriptions"
                title="Job Descriptions"
                description="Full public descriptions for each open role. Linked to a Job Role."
                actions={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={openDesign}
                            data-testid="jd-open-design"
                            className="inline-flex items-center gap-2 px-4 h-11 border border-[#0F1F38]/25 hover:border-[#0F1F38] bg-white text-xs tracking-label uppercase font-semibold transition-colors"
                        >
                            <Settings2 size={14} /> JD Design
                        </button>
                        <button
                            onClick={openCreate}
                            data-testid="jd-add-button"
                            className="inline-flex items-center gap-2 px-5 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                        >
                            <Plus size={14} /> Add Job Description
                        </button>
                    </div>
                }
            />

            <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
                {loading ? (
                    <div className="flex items-center gap-3 text-sm text-[#4B5563]">
                        <Loader2 size={16} className="animate-spin" />
                        Loading job descriptions…
                    </div>
                ) : descriptions.length === 0 ? (
                    <div
                        data-testid="jd-empty"
                        className="bg-white border border-dashed border-[#0F1F38]/20 py-20 px-8 text-center flex flex-col items-center"
                    >
                        <div className="w-12 h-12 flex items-center justify-center bg-[#F7F5F0] border border-[#0F1F38]/10 mb-6">
                            <FileText
                                size={20}
                                strokeWidth={1.5}
                                className="text-[#0F1F38]"
                            />
                        </div>
                        <div className="font-display text-3xl leading-tight text-[#0F1F38]">
                            No job descriptions yet.
                        </div>
                        <p className="mt-3 max-w-md text-sm text-[#4B5563]">
                            Write the first public description candidates
                            will read.
                        </p>
                    </div>
                ) : (
                    <div
                        data-testid="jd-grid"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        {descriptions.map((jd) => (
                            <div
                                key={jd.id}
                                data-testid={`jd-card-${jd.id}`}
                                className="bg-white border border-[#0F1F38]/10 p-6 flex flex-col gap-4 hover:border-[#0F1F38]/40 transition-colors"
                            >
                                <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/50">
                                    {roleMap[jd.job_role_id]?.name || "Unknown role"}
                                </div>
                                <div className="font-display text-2xl leading-tight text-[#0F1F38] break-words">
                                    {jd.job_title}
                                </div>
                                <div className="text-xs text-[#4B5563]">
                                    {jd.sections?.length || 0} section
                                    {jd.sections?.length === 1 ? "" : "s"}
                                </div>
                                <div className="mt-auto flex items-center flex-wrap gap-2 pt-2">
                                    <button
                                        onClick={() => openView(jd)}
                                        data-testid={`jd-view-${jd.id}`}
                                        className="inline-flex items-center gap-2 px-3 h-9 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-xs tracking-label uppercase font-semibold transition-colors"
                                    >
                                        <Eye size={12} /> View
                                    </button>
                                    <button
                                        onClick={() => openEdit(jd)}
                                        data-testid={`jd-edit-${jd.id}`}
                                        className="inline-flex items-center gap-2 px-3 h-9 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-xs tracking-label uppercase font-semibold transition-colors"
                                    >
                                        <Pencil size={12} /> Edit
                                    </button>
                                    <button
                                        onClick={() => onDelete(jd)}
                                        data-testid={`jd-delete-${jd.id}`}
                                        className="inline-flex items-center gap-2 px-3 h-9 border border-[#0F1F38]/20 hover:border-red-700 hover:text-red-700 text-xs tracking-label uppercase font-semibold transition-colors"
                                    >
                                        <Trash2 size={12} /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create / Edit Modal */}
            <Dialog
                open={dialogOpen}
                onOpenChange={(o) => (o ? setDialogOpen(true) : closeDialog())}
            >
                <DialogContent
                    data-testid="jd-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                            {editing ? "Edit description" : "New description"}
                        </div>
                        <DialogTitle className="font-display text-3xl leading-tight text-[#0F1F38]">
                            {editing
                                ? "Edit Job Description"
                                : "Add Job Description"}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#4B5563]">
                            Compose the description in labelled sections.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={onSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Job Role
                                </Label>
                                <Select
                                    value={form.job_role_id}
                                    onValueChange={(v) => {
                                        setForm((f) => ({
                                            ...f,
                                            job_role_id: v,
                                        }));
                                        if (error) setError("");
                                    }}
                                >
                                    <SelectTrigger
                                        data-testid="jd-role-select"
                                        className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white"
                                    >
                                        <SelectValue placeholder="Select a job role" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {roles.map((r) => (
                                            <SelectItem
                                                key={r.id}
                                                value={r.id}
                                                data-testid={`jd-role-option-${r.id}`}
                                            >
                                                {r.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Job Title
                                </Label>
                                <Input
                                    value={form.job_title}
                                    onChange={(e) => {
                                        setForm((f) => ({
                                            ...f,
                                            job_title: e.target.value,
                                        }));
                                        if (error) setError("");
                                    }}
                                    placeholder="e.g. Senior Backend Engineer, Payments"
                                    data-testid="jd-title-input"
                                    className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                Encouragement callout
                            </Label>
                            <Textarea
                                value={form.callout_text || ""}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        callout_text: e.target.value,
                                    }))
                                }
                                placeholder="Short encouraging note shown under the header. Leave blank to hide."
                                rows={2}
                                data-testid="jd-callout-input"
                                className="mt-2 rounded-none border-[#0F1F38]/25 bg-white resize-y"
                            />
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Header Section · {form.header_rows.length}
                                </Label>
                                <button
                                    type="button"
                                    onClick={addHeaderRow}
                                    data-testid="jd-add-header-row"
                                    className="inline-flex items-center gap-2 px-3 h-9 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-xs tracking-label uppercase font-semibold transition-colors bg-white"
                                >
                                    <Plus size={12} /> Add pair
                                </button>
                            </div>
                            {form.header_rows.length === 0 ? (
                                <div className="bg-white border border-dashed border-[#0F1F38]/20 px-4 py-6 text-xs text-[#4B5563]">
                                    Add labelled pairs (e.g. Type: Full-time,
                                    Mode: Hybrid, City: Chennai). Only the
                                    right-side values will be displayed
                                    pipe-separated below the role name (e.g.
                                    &quot;Full-time | Hybrid | Chennai&quot;).
                                </div>
                            ) : (
                                <div className="bg-white border border-[#0F1F38]/15">
                                    <div className="hidden md:grid grid-cols-[220px_1fr_44px] bg-[#F7F5F0] border-b border-[#0F1F38]/15">
                                        <div className="px-4 py-2 text-[10px] tracking-label uppercase text-[#0F1F38]/60 border-r border-[#0F1F38]/15">
                                            Label
                                        </div>
                                        <div className="px-4 py-2 text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                            Value (shown pipe-separated)
                                        </div>
                                        <div />
                                    </div>
                                    {form.header_rows.map((r, idx) => (
                                        <div
                                            key={r._rid}
                                            data-testid={`jd-header-row-${idx}`}
                                            draggable
                                            onDragStart={(e) => {
                                                headerDragIdx.current = idx;
                                                e.dataTransfer.effectAllowed =
                                                    "move";
                                            }}
                                            onDragOver={(e) =>
                                                e.preventDefault()
                                            }
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                reorder(
                                                    "header_rows",
                                                    headerDragIdx.current,
                                                    idx,
                                                );
                                                headerDragIdx.current = null;
                                            }}
                                            className={`grid grid-cols-[28px_1fr] md:grid-cols-[28px_220px_1fr_44px] items-stretch ${
                                                idx === 0
                                                    ? ""
                                                    : "border-t border-[#0F1F38]/15"
                                            }`}
                                        >
                                            <div className="flex items-center justify-center text-[#0F1F38]/40 cursor-grab active:cursor-grabbing">
                                                <GripVertical size={14} />
                                            </div>
                                            <Input
                                                value={r.label}
                                                onChange={(e) =>
                                                    updateHeaderRow(
                                                        r._rid,
                                                        { label: e.target.value },
                                                    )
                                                }
                                                placeholder="Type"
                                                data-testid={`jd-header-label-${idx}`}
                                                className="h-11 rounded-none border-0 md:border-r md:border-[#0F1F38]/15 bg-white focus-visible:ring-0 focus-visible:ring-offset-0"
                                            />
                                            <Input
                                                value={r.value}
                                                onChange={(e) =>
                                                    updateHeaderRow(
                                                        r._rid,
                                                        { value: e.target.value },
                                                    )
                                                }
                                                placeholder="Full-time"
                                                data-testid={`jd-header-value-${idx}`}
                                                className="h-11 rounded-none border-0 border-t md:border-t-0 border-[#0F1F38]/15 bg-white focus-visible:ring-0 focus-visible:ring-offset-0"
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeHeaderRow(r._rid)
                                                }
                                                data-testid={`jd-header-remove-${idx}`}
                                                className="flex items-center justify-center h-11 md:h-auto md:border-l md:border-[#0F1F38]/15 text-[#0F1F38]/50 hover:text-red-700 transition-colors"
                                                aria-label="Remove pair"
                                            >
                                                <Trash size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Body Header Section · {form.about_rows.length}
                                </Label>
                                <button
                                    type="button"
                                    onClick={addAboutRow}
                                    data-testid="jd-add-about-row"
                                    className="inline-flex items-center gap-2 px-3 h-9 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-xs tracking-label uppercase font-semibold transition-colors bg-white"
                                >
                                    <Plus size={12} /> Add section
                                </button>
                            </div>
                            {form.about_rows.length === 0 ? (
                                <div className="bg-white border border-dashed border-[#0F1F38]/20 px-4 py-6 text-xs text-[#4B5563]">
                                    Add labelled pairs (e.g. Experience: 2-4
                                    Years, Salary: 5-7 LPA) to appear
                                    as a grid at the top of the JD. Both label
                                    and value are shown.
                                </div>
                            ) : (
                                <div className="bg-white border border-[#0F1F38]/15">
                                    <div className="hidden md:grid grid-cols-[220px_1fr_44px] bg-[#F7F5F0] border-b border-[#0F1F38]/15">
                                        <div className="px-4 py-2 text-[10px] tracking-label uppercase text-[#0F1F38]/60 border-r border-[#0F1F38]/15">
                                            Label
                                        </div>
                                        <div className="px-4 py-2 text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                            Value
                                        </div>
                                        <div />
                                    </div>
                                    {form.about_rows.map((r, idx) => (
                                        <div
                                            key={r._rid}
                                            data-testid={`jd-about-row-${idx}`}
                                            draggable
                                            onDragStart={(e) => {
                                                aboutDragIdx.current = idx;
                                                e.dataTransfer.effectAllowed =
                                                    "move";
                                            }}
                                            onDragOver={(e) =>
                                                e.preventDefault()
                                            }
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                reorder(
                                                    "about_rows",
                                                    aboutDragIdx.current,
                                                    idx,
                                                );
                                                aboutDragIdx.current = null;
                                            }}
                                            className={`grid grid-cols-[28px_1fr] md:grid-cols-[28px_220px_1fr_auto_44px] items-stretch ${
                                                idx === 0
                                                    ? ""
                                                    : "border-t border-[#0F1F38]/15"
                                            }`}
                                        >
                                            <div className="flex items-center justify-center text-[#0F1F38]/40 cursor-grab active:cursor-grabbing">
                                                <GripVertical size={14} />
                                            </div>
                                            <Input
                                                value={r.label}
                                                onChange={(e) =>
                                                    updateAboutRow(r._rid, {
                                                        label: e.target
                                                            .value,
                                                    })
                                                }
                                                placeholder="Role"
                                                data-testid={`jd-about-label-${idx}`}
                                                className="h-11 rounded-none border-0 md:border-r md:border-[#0F1F38]/15 bg-white focus-visible:ring-0 focus-visible:ring-offset-0"
                                            />
                                            <Input
                                                value={r.value}
                                                onChange={(e) =>
                                                    updateAboutRow(r._rid, {
                                                        value: e.target
                                                            .value,
                                                    })
                                                }
                                                placeholder="AI & ML Engineer"
                                                data-testid={`jd-about-value-${idx}`}
                                                className="h-11 rounded-none border-0 border-t md:border-t-0 border-[#0F1F38]/15 bg-white focus-visible:ring-0 focus-visible:ring-offset-0"
                                            />
                                            <div className="hidden md:flex items-center gap-1 px-2 border-l border-[#0F1F38]/15 bg-white" />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeAboutRow(r._rid)
                                                }
                                                data-testid={`jd-about-remove-${idx}`}
                                                className="flex items-center justify-center h-11 md:h-auto md:border-l md:border-[#0F1F38]/15 text-[#0F1F38]/50 hover:text-red-700 transition-colors"
                                                aria-label="Remove row"
                                            >
                                                <Trash size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Description sections · {form.sections.length}
                                </Label>
                                <button
                                    type="button"
                                    onClick={addSection}
                                    data-testid="jd-add-section"
                                    className="inline-flex items-center gap-2 px-3 h-9 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-xs tracking-label uppercase font-semibold transition-colors bg-white"
                                >
                                    <Plus size={12} /> Add section
                                </button>
                            </div>

                            <div className="space-y-3">
                                {form.sections.map((s, idx) => (
                                    <div
                                        key={s._rid}
                                        data-testid={`jd-section-${idx}`}
                                        draggable
                                        onDragStart={(e) => {
                                            sectionDragIdx.current = idx;
                                            e.dataTransfer.effectAllowed =
                                                "move";
                                        }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            reorder(
                                                "sections",
                                                sectionDragIdx.current,
                                                idx,
                                            );
                                            sectionDragIdx.current = null;
                                        }}
                                        className="bg-white border border-[#0F1F38]/15 p-4 space-y-3"
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="flex items-center justify-center w-6 text-[#0F1F38]/40 cursor-grab active:cursor-grabbing">
                                                <GripVertical size={14} />
                                            </div>
                                            <Input
                                                value={s.heading}
                                                onChange={(e) =>
                                                    updateSection(s._rid, {
                                                        heading:
                                                            e.target.value,
                                                    })
                                                }
                                                placeholder="Section heading (e.g. Responsibilities)"
                                                data-testid={`jd-section-heading-${idx}`}
                                                className="h-10 rounded-none border-[#0F1F38]/20 bg-white"
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeSection(s._rid)
                                                }
                                                data-testid={`jd-section-remove-${idx}`}
                                                className="p-2 text-[#0F1F38]/50 hover:text-red-700 transition-colors"
                                                aria-label="Remove section"
                                            >
                                                <Trash size={14} />
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <Textarea
                                                ref={setSectionRef(s._rid)}
                                                value={s.content}
                                                onChange={(e) =>
                                                    updateSection(s._rid, {
                                                        content:
                                                            e.target.value,
                                                    })
                                                }
                                                placeholder="Section content…"
                                                rows={4}
                                                data-testid={`jd-section-content-${idx}`}
                                                className="rounded-none border-[#0F1F38]/20 bg-white resize-y pr-12"
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    bulletifySection(s._rid)
                                                }
                                                data-testid={`jd-section-bulletify-${idx}`}
                                                aria-label="Bullet points"
                                                title="Bullet selected paragraph, or insert a new bullet"
                                                className="absolute top-2 right-2 inline-flex items-center justify-center w-8 h-8 bg-white border border-[#0F1F38]/20 text-[#0F1F38]/70 hover:text-[#0F1F38] hover:border-[#0F1F38] transition-colors"
                                            >
                                                <List size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div
                                data-testid="jd-dialog-error"
                                className="text-sm text-red-700 border-l-2 border-red-700 pl-3"
                            >
                                {error}
                            </div>
                        )}

                        <div className="flex items-center justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={closeDialog}
                                data-testid="jd-dialog-cancel"
                                className="px-5 h-11 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="jd-dialog-submit"
                                disabled={submitting}
                                className="px-6 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                            >
                                {submitting && (
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                )}
                                {editing ? "Save" : "Create"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Global JD Design Settings — writes to /jd-design, applied to
                every JD across the whole app. */}
            <Dialog open={designOpen} onOpenChange={setDesignOpen}>
                <DialogContent
                    data-testid="jd-design-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                            Design system · Global
                        </div>
                        <DialogTitle className="font-display text-2xl leading-tight text-[#0F1F38]">
                            JD Design Settings
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#4B5563]">
                            One shared design for every Job Description. Set
                            fonts, sizes, Apply button behaviour once — every
                            JD page inherits it.
                        </DialogDescription>
                    </DialogHeader>

                    {/* Apply button controls sit above the style tabs */}
                    <div className="bg-white border border-[#0F1F38]/15 p-4 mb-4 space-y-4">
                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                            Apply button (global)
                        </div>
                        <div>
                            <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                Button text
                            </Label>
                            <Input
                                value={draftDesign?.apply_button_text || ""}
                                onChange={(e) =>
                                    setDraftDesign((d) => ({
                                        ...d,
                                        apply_button_text: e.target.value,
                                    }))
                                }
                                placeholder="Apply Now"
                                data-testid="jd-design-apply-text"
                                className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                        Show top button
                                    </Label>
                                    <Switch
                                        checked={!!draftDesign?.show_top_apply}
                                        onCheckedChange={(v) =>
                                            setDraftDesign((d) => ({
                                                ...d,
                                                show_top_apply: v,
                                            }))
                                        }
                                        data-testid="jd-design-toggle-top"
                                    />
                                </div>
                                <div className="mt-3 text-[11px] text-[#4B5563] leading-relaxed">
                                    Top button sits inline with the job title
                                    on the right side of the same row.
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                        Show bottom button
                                    </Label>
                                    <Switch
                                        checked={
                                            !!draftDesign?.show_bottom_apply
                                        }
                                        onCheckedChange={(v) =>
                                            setDraftDesign((d) => ({
                                                ...d,
                                                show_bottom_apply: v,
                                            }))
                                        }
                                        data-testid="jd-design-toggle-bottom"
                                    />
                                </div>
                                <Select
                                    value={
                                        draftDesign?.bottom_apply_align ||
                                        "center"
                                    }
                                    onValueChange={(v) =>
                                        setDraftDesign((d) => ({
                                            ...d,
                                            bottom_apply_align: v,
                                        }))
                                    }
                                >
                                    <SelectTrigger
                                        data-testid="jd-design-bottom-align"
                                        disabled={
                                            !draftDesign?.show_bottom_apply
                                        }
                                        className="mt-3 h-11 rounded-none border-[#0F1F38]/25 bg-white disabled:opacity-50"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="left">Left</SelectItem>
                                        <SelectItem value="center">Center</SelectItem>
                                        <SelectItem value="right">Right</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-1 mb-3 pb-3 border-b border-[#0F1F38]/10">
                        {DESIGN_TABS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setDesignTab(t.id)}
                                data-testid={`jd-design-tab-${t.id}`}
                                className={`px-3 h-8 text-[10px] tracking-label uppercase font-semibold border transition-colors ${
                                    designTab === t.id
                                        ? "bg-[#0F1F38] text-[#F7F5F0] border-[#0F1F38]"
                                        : "bg-white border-[#0F1F38]/20 hover:border-[#0F1F38] text-[#0F1F38]"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div data-testid={`jd-design-panel-${designTab}`}>
                        <StyleControls
                            value={
                                (draftDesign?.styles || {})[designTab] || {}
                            }
                            onChange={(s) => patchDraftStyle(designTab, s)}
                            testidPrefix={`jd-design-${designTab}`}
                        />
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#0F1F38]/10 mt-4">
                        <button
                            type="button"
                            onClick={() => setDesignOpen(false)}
                            data-testid="jd-design-cancel"
                            className="px-5 h-10 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={saveDesign}
                            disabled={designSaving}
                            data-testid="jd-design-save"
                            className="px-6 h-10 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                        >
                            {designSaving && (
                                <Loader2 size={13} className="animate-spin" />
                            )}
                            Save Design
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* View Modal */}
            <Dialog open={viewOpen} onOpenChange={setViewOpen}>
                <DialogContent
                    data-testid="jd-view-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto"
                >
                    {viewing && (
                        <>
                            <DialogHeader>
                                <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    {roleMap[viewing.job_role_id]?.name ||
                                        "Unknown role"}
                                </div>
                                <DialogTitle
                                    className="font-display text-3xl leading-tight text-[#0F1F38]"
                                    style={jdCss(savedDesign?.styles, "title")}
                                >
                                    {viewing.job_title}
                                </DialogTitle>
                            </DialogHeader>
                            <div
                                className="space-y-8"
                                style={jdCss(savedDesign?.styles)}
                            >
                                {(viewing.about_rows || []).length > 0 && (
                                    <div
                                        data-testid="jd-view-about-table"
                                        className="border border-[#0F1F38]/15 bg-white"
                                    >
                                        {(viewing.about_rows || []).map(
                                            (r, i) => (
                                                <div
                                                    key={i}
                                                    className={`grid grid-cols-[180px_1fr] ${
                                                        i === 0
                                                            ? ""
                                                            : "border-t border-[#0F1F38]/15"
                                                    }`}
                                                >
                                                    <div
                                                        className="px-4 py-3 border-r border-[#0F1F38]/15"
                                                        style={jdCss(
                                                            savedDesign?.styles,
                                                            "body_header_label",
                                                        )}
                                                    >
                                                        {r.label}
                                                    </div>
                                                    <div
                                                        className="px-4 py-3 whitespace-pre-wrap"
                                                        style={jdCss(
                                                            savedDesign?.styles,
                                                            "body_header_value",
                                                        )}
                                                    >
                                                        {r.value}
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                )}
                                {(viewing.sections || []).map((s, i) => (
                                    <div
                                        key={i}
                                        data-testid={`jd-view-section-${i}`}
                                    >
                                        <h3
                                            style={jdCss(
                                                savedDesign?.styles,
                                                "section_heading",
                                            )}
                                            className="mb-3"
                                        >
                                            {s.heading || "Untitled section"}
                                        </h3>
                                        <div
                                            className="whitespace-pre-wrap"
                                            style={jdCss(
                                                savedDesign?.styles,
                                                "section_content",
                                            )}
                                        >
                                            {s.content}
                                        </div>
                                    </div>
                                ))}
                                {(!viewing.sections ||
                                    viewing.sections.length === 0) && (
                                    <p className="text-sm text-[#4B5563]">
                                        No sections yet.
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
